// A full-screen player for one short video, opened from a card. The video
// never plays until the viewer opens it, and plays with sound.

import { useEffect, useRef, type TouchEvent } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";

import {
  backdropAlpha,
  dragOffset,
  startsOnControlStrip,
  swipeDismisses,
} from "./swipe";

export type VideoCloseVia =
  "close_button" | "escape" | "backdrop" | "swipe" | "ended";

export interface VideoCloseInfo {
  via: VideoCloseVia;
  /** The furthest point reached, in whole seconds. */
  secondsWatched: number;
  /** The video played to its end. */
  completed: boolean;
}

interface VideoLightboxProps {
  open: boolean;
  /** In preference order; the browser plays the first it supports. */
  sources: { src: string; type: string }[];
  poster: string;
  /** The dialog's accessible name. */
  title: string;
  closeLabel: string;
  /** The element focus returns to on close — the card that opened the
   * lightbox, which some browsers never focus on click. */
  returnFocusTo?: HTMLElement | null;
  onClose: (info: VideoCloseInfo) => void;
}

const FOCUSABLE = "button, video";

export default function VideoLightbox({
  open,
  sources,
  poster,
  title,
  closeLabel,
  returnFocusTo = null,
  onClose,
}: VideoLightboxProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const furthestRef = useRef(0);
  const swipeRef = useRef<{ startY: number; startedAt: number } | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const returnFocusRef = useRef(returnFocusTo);
  returnFocusRef.current = returnFocusTo;

  const close = (via: VideoCloseVia) => {
    const video = videoRef.current;
    video?.pause();
    onCloseRef.current({
      via,
      secondsWatched: Math.floor(furthestRef.current),
      completed: via === "ended",
    });
  };
  const closeRefFn = useRef(close);
  closeRefFn.current = close;

  useEffect(() => {
    if (!open) return;
    furthestRef.current = 0;
    const opener = returnFocusRef.current ?? document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    // The tap that opened the lightbox is the user gesture that lets
    // playback start with sound; a refusal leaves the controls to the
    // viewer.
    videoRef.current?.play().catch(() => undefined);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRefFn.current("escape");
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (
        event.shiftKey &&
        (active === first || !dialogRef.current?.contains(active))
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (active === last || !dialogRef.current?.contains(active))
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, [open]);

  if (!open) return null;

  const resetDrag = () => {
    swipeRef.current = null;
    const frame = frameRef.current;
    if (frame) {
      frame.style.transform = "";
      frame.style.transition = "";
    }
    if (dialogRef.current) dialogRef.current.style.backgroundColor = "";
  };

  const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (event.touches.length !== 1) return;
    const frame = frameRef.current;
    if (!frame) return;
    const y = event.touches[0].clientY;
    if (startsOnControlStrip(y, frame.getBoundingClientRect().bottom)) return;
    swipeRef.current = { startY: y, startedAt: Date.now() };
    frame.style.transition = "none";
  };

  const onTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    const swipe = swipeRef.current;
    const frame = frameRef.current;
    if (!swipe || !frame) return;
    const distance = dragOffset(swipe.startY, event.touches[0].clientY);
    frame.style.transform = `translateY(${distance}px)`;
    if (dialogRef.current) {
      dialogRef.current.style.backgroundColor = `rgba(20,16,15,${backdropAlpha(distance)})`;
    }
  };

  const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    const swipe = swipeRef.current;
    if (!swipe) return;
    const distance = dragOffset(swipe.startY, event.changedTouches[0].clientY);
    const dismiss = swipeDismisses(distance, Date.now() - swipe.startedAt);
    resetDrag();
    if (dismiss) close("swipe");
  };

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(20,16,15,0.92)] pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]"
      onClick={(event) => {
        if (event.target === event.currentTarget) close("backdrop");
      }}
    >
      <div
        ref={frameRef}
        className="relative aspect-[9/16] h-[min(88vh,calc(100vw*16/9))] max-w-[100vw] motion-safe:transition-transform motion-safe:duration-200 max-[520px]:aspect-auto max-[520px]:h-full max-[520px]:w-full"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={resetDrag}
      >
        <video
          ref={videoRef}
          poster={poster}
          controls
          playsInline
          preload="none"
          className="block h-full w-full rounded-2xl bg-black object-contain max-[520px]:rounded-none"
          onTimeUpdate={(event) => {
            furthestRef.current = Math.max(
              furthestRef.current,
              event.currentTarget.currentTime,
            );
          }}
          onEnded={(event) => {
            furthestRef.current = Math.max(
              furthestRef.current,
              event.currentTarget.duration || 0,
            );
            event.currentTarget.currentTime = 0;
            close("ended");
          }}
        >
          {sources.map((source) => (
            <source key={source.src} src={source.src} type={source.type} />
          ))}
        </video>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-2.5 -ml-5 hidden h-[5px] w-10 rounded-[3px] bg-white/70 [@media(hover:none)_and_(pointer:coarse)]:block"
        />
        <button
          ref={closeRef}
          type="button"
          aria-label={closeLabel}
          onClick={() => close("close_button")}
          className="absolute right-2 top-2 flex h-12 w-12 items-center justify-center rounded-full bg-black/55 p-0 text-white focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <XMarkIcon className="h-7 w-7" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
